import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
// import {
//   reForwardOrder,
//   reForwardOrderInCancel,
// } from "src/api/server/prepacks";
import db from "src/db";
import { KTSVG } from "src/resources/helpers";
import { Timestamp } from "firebase/firestore";
import moment from "moment";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { toast } from "react-toastify";
import { startOfDay, endOfDay, format, parseISO, subDays } from "date-fns";

const LoadingSpinner = () => (
  <div className="spinner-border spinner-border-sm text-primary" role="status">
    <span className="visually-hidden">Loading...</span>
  </div>
);

/**
 * ReprintModalProps Interface
 */
type ReprintModalProps = {
  onClose: () => void;
  onSubmit?: (data: { locator: string; sku: string }) => void;
};

/**
 * ReprintModal is a modal component that allows the user to scan a locator and input a SKU.
 * It will validate the locator and SKU and submit the form if both are valid.
 * It also handles errors and displays them to the user.
 *
 * @param {function} onClose - A function to call when the modal is closed.
 * @param {function} onSubmit - A function to call when the form is submitted.
 * @returns {JSX.Element} - The modal component.
 */
const ReprintModal: React.FC<ReprintModalProps> = ({ onClose, onSubmit }) => {
  const { t } = useTranslation();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [orders, setOrders] = useState<any[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<any[]>([]);
  const [error, setError] = useState<any>(null);

  // Use date-fns to get today's start (midnight)
  const todayStart = startOfDay(new Date());
  // Calculate minimum date (30 days ago)
  const minDate = startOfDay(subDays(new Date(), 30));

  const [startDate, setStartDate] = useState<Date>(todayStart);
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [isFilterActive, setIsFilterActive] = useState<boolean>(false);

  const fetchOrders = async (start: Date, end: Date) => {
    try {
      setSearchLoading(true);
      setError(null);

      const startTimestamp = Timestamp.fromDate(start);
      const endTimestamp = Timestamp.fromDate(end);

      // Query ke Firestore untuk mendapatkan orders
      const querySnapshot = await db
        .collection("orders")
        .where("printedLabelAt", ">=", startTimestamp)
        .where("printedLabelAt", "<=", endTimestamp)
        .orderBy("printedLabelAt", "desc")
        .get();

      const orders = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Ambil productSKU dan cari productName
      const fetchedOrders = await Promise.all(
        orders.map(async (order: any) => {
          const productSKU = order?.itemList?.[0]?.itemSKU || "";
          let productName = "Unknown Product";

          if (productSKU) {
            try {
              const productDoc = await db
                .collection("products")
                .where("sku", "==", productSKU)
                .limit(1)
                .get();

              if (!productDoc.empty) {
                productName =
                  productDoc.docs[0].data().name || "Unknown Product";
              }
            } catch (productError) {
              console.error("Error fetching product:", productError);
            }
          }

          return {
            ...order,
            productSKU,
            productName,
          };
        })
      );

      setOrders(fetchedOrders);
      setFilteredOrders(fetchedOrders);
    } catch (err) {
      setError("Error fetching orders.");
      console.error("Error fetching orders:", err);
    } finally {
      setSearchLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders(startOfDay(startDate), endOfDay(endDate));
  }, []);

  // Handle date filter change
  const handleApplyFilter = () => {
    setIsFilterActive(true);

    // Use date-fns endOfDay to correctly set the end date
    const dayEnd = endOfDay(endDate);

    fetchOrders(startDate, dayEnd);
  };

  const handleResetFilter = () => {
    // Use date-fns for consistent date handling
    const today = new Date();
    const todayAtStart = startOfDay(today);

    setStartDate(todayAtStart);
    setEndDate(today);
    setIsFilterActive(false);

    fetchOrders(todayAtStart, today);
  };

  useEffect(() => {
    if (!searchTerm) {
      setFilteredOrders(orders);
    } else {
      const lowercasedSearch = searchTerm.toLowerCase();
      setFilteredOrders(
        orders.filter(
          (order) =>
            order.ordersn?.toLowerCase().includes(lowercasedSearch) ||
            order.productSKU?.toLowerCase().includes(lowercasedSearch) ||
            order.productName?.toLowerCase().includes(lowercasedSearch) ||
            order.printedLabelLocatorCode
              ?.toLowerCase()
              .includes(lowercasedSearch) ||
            order.shippingTrackingNumber
              ?.toLowerCase()
              .includes(lowercasedSearch)
        )
      );
    }
  }, [searchTerm, orders]);

  const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const ForwardInfoComponent = ({ data }: { data: any }) => {
    const key = `${data.id}-forward-error-tooltip`;

    if (data.isForwardSuccess == null) {
      return (
        <div className="m-0 p-0 d-flex justify-content-start fw-bolder">
          <OverlayTrigger
            placement="top"
            delay={{ show: 250, hide: 400 }}
            overlay={(props) => (
              <Tooltip {...props} id={key} className="fs-5">
                {t("OrderList.ForwardError.OrderNotForwarded")}
              </Tooltip>
            )}
          >
            <i className="bi bi-info-circle-fill text-warning fs-1"></i>
          </OverlayTrigger>
        </div>
      );
    }

    // If forwardError is false it means that the order has been forwarded successfully
    if (!data.forwardError && data.isForwardSuccess) {
      return (
        <div className="m-0 p-0 d-flex justify-content-start fw-bolder">
          <i className="bi bi-check-circle-fill text-success fs-1"></i>
        </div>
      );
    }

    // If isForwardError is true it means that the order has not been forwarded
    return (
      <div className="m-0 p-0 d-flex justify-content-start fw-bolder">
        <OverlayTrigger
          placement="top"
          delay={{ show: 250, hide: 400 }}
          overlay={(props) => (
            <Tooltip {...props} id={key} className="fs-5">
              {data.forwardError ?? t("OrderList.ForwardError.DefaultError")}
            </Tooltip>
          )}
        >
          <i className="bi bi-exclamation-circle-fill text-danger fs-1"></i>
        </OverlayTrigger>
      </div>
    );
  };

  const exportToCSV = () => {
    const csvRows = [];
    const headers = [
      "ordersn",
      "shippingTrackingNumber",
      "productSKU",
      "productName",
      "printedLabel",
      "printedLabelLocatorCode",
      "forwardStatus",
    ];
    csvRows.push(headers.join(","));

    filteredOrders.forEach((order) => {
      const row = [
        order.ordersn,
        order.shippingTrackingNumber,
        order.productSKU,
        order.productName,
        moment(order.printedLabelAt.toDate()).format("YYYY-MM-DD HH:mm:ss"),
        order.printedLabelLocatorCode ?? "-",
        order.isForwardSuccess == null
          ? "Not Forwarded Yet"
          : !order.forwardError && order.isForwardSuccess
          ? "Success"
          : order.forwardError ?? "Failed To Forward",
      ];
      csvRows.push(row.join(","));
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `printed-vp-orders-${moment().format("DD-MM-YYYY")}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <div className="modal-backdrop show"></div>
      <div className="modal show d-block">
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content">
            {/* Modal Header */}
            <div className="modal-header py-3">
              <h5 className="modal-title m-0 p-0">
                {t("Value.Pack.Scan.Reprint")}
              </h5>
              <button
                className="btn btn-sm btn-light m-0 p-0"
                onClick={onClose}
              >
                <KTSVG
                  path="/media/icons/duotune/arrows/arr061.svg"
                  className="svg-icon svg-icon-2x"
                />
              </button>
            </div>

            {/* Modal Body */}
            <div className="modal-body py-0 my-2">
              {/* Date Filter Section */}
              <div
                className="mb-3 bg-light p-3 border rounded z-3"
                style={{ position: "relative", zIndex: 1100 }}
              >
                <div className="d-flex flex-wrap align-items-center gap-3">
                  <div className="d-flex align-items-center">
                    <label className="me-2 mb-0 fw-bold">
                      {t("OrderList.Sync.FirstDate")}:
                    </label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={format(startDate, "yyyy-MM-dd")}
                      min={format(minDate, "yyyy-MM-dd")}
                      max={format(endDate, "yyyy-MM-dd")}
                      onChange={(e) => {
                        if (e.target.value) {
                          // Use date-fns to ensure consistent date handling
                          const date = startOfDay(parseISO(e.target.value));
                          setStartDate(date);
                        }
                      }}
                    />
                  </div>
                  <div className="d-flex align-items-center">
                    <label className="me-2 mb-0 fw-bold">
                      {t("OrderList.Sync.EndDate")}:
                    </label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={format(endDate, "yyyy-MM-dd")}
                      min={format(startDate, "yyyy-MM-dd")}
                      max={format(new Date(), "yyyy-MM-dd")}
                      onChange={(e) => {
                        if (e.target.value) {
                          // Use endOfDay for end date to include the full day
                          const date = endOfDay(parseISO(e.target.value));
                          setEndDate(date);
                        }
                      }}
                    />
                  </div>
                  <div className="ms-auto">
                    <button
                      className="btn btn-sm btn-primary me-2"
                      onClick={handleApplyFilter}
                      disabled={searchLoading}
                    >
                      {t("Common.ApplyFilter")}
                    </button>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={handleResetFilter}
                      disabled={searchLoading || !isFilterActive}
                    >
                      {t("Common.ResetFilter")}
                    </button>
                  </div>
                </div>
              </div>

              {/* Search Input */}
              <div className="mb-2 text-start">
                <div className="input-group py-0">
                  {/* Icon Search */}
                  <span className="input-group-text h-100 m-0">
                    <KTSVG
                      path="/media/icons/duotune/general/gen021.svg"
                      className="svg-icon"
                    />
                  </span>

                  {/* Input */}
                  <input
                    id="search-input"
                    ref={searchInputRef}
                    type="text"
                    className={`form-control ${
                      error ? "is-invalid" : ""
                    } my-0 h-100`}
                    value={searchTerm}
                    onChange={handleSearchInput}
                    placeholder={t("Value.Pack.Search.Placeholder")}
                  />

                  {/* Loading Icon */}
                  {searchLoading && (
                    <span className="input-group-text m-0 h-100">
                      <LoadingSpinner />
                    </span>
                  )}

                  {error && <div className="invalid-feedback">{error}</div>}
                </div>
              </div>

              <p className="m-0 py-0 text-sm">
                {orders?.length} {t("Value.Pack.Info.Printed")}{" "}
                {isFilterActive
                  ? `${format(startDate, "dd MMM yyyy")} - ${format(
                      endDate,
                      "dd MMM yyyy"
                    )}`
                  : format(new Date(), "dd MMMM yyyy")}
                {filteredOrders?.length > 0 && searchTerm !== "" && (
                  <span>
                    {" ( "} Search for {searchTerm} : {filteredOrders.length}{" "}
                    {t("Value.Pack.Breadcrumb")}
                    {" )"}
                  </span>
                )}
              </p>

              {/* Orders List */}
              <div
                className="table-responsive mt-2 border border-gray border-2 rounded"
                style={{
                  maxHeight: "320px",
                  overflowY: "auto",
                }}
              >
                <table className="table z-1">
                  <thead className="sticky-top bg-secondary text-dark">
                    <tr className="align-middle">
                      <th className="w-auto text-wrap">
                        {t("OrderList.OrderNumber")}
                      </th>
                      <th className="w-auto text-wrap">
                        {t("HandledCustomer.Modal.Invoice.TrackingNumber")}
                      </th>
                      <th className="w-auto text-wrap">
                        {t("Value.Pack.ProductName")}
                      </th>
                      <th className="w-auto text-wrap">
                        {t("Value.Pack.ProductSKU")}
                      </th>
                      <th className="w-auto text-wrap">
                        {t("Value.Pack.Printed")}
                      </th>
                      <th className="w-auto text-wrap">
                        {t("Value.Pack.PrintedLocator")}
                      </th>
                      {/* <th className="w-auto text-wrap">
                        {t("OrderList.ForwardError")}
                      </th>{" "} */}
                      <th className="w-auto text-wrap">
                        {t("Common.Modal.Column.Header.Status")}
                      </th>
                      <th className="w-auto text-wrap">
                        {t("Common.Actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.length > 0 ? (
                      filteredOrders.map((order) => (
                        <tr
                          key={order.id}
                          style={{
                            backgroundColor:
                              order?.status === "CANCELLED"
                                ? "#f8d7da" // light red
                                : order?.status === "IN_CANCEL"
                                ? "#fff3cd" // light yellow
                                : order?.status === "SHIPPING" ||
                                  order?.status === "COMPLETED" ||
                                  order?.status === "DELIVERED"
                                ? "#d4edda" // light green
                                : "#fff",
                            position: "relative",
                          }}
                          className="align-middle border-bottom border-gray border-2 py-1 my-1 text-sm"
                        >
                          <td className="w-auto text-wrap">
                            {order.ordersn}
                            {/* {order?.status === "CANCELLED" && (
                              <div
                                style={{
                                  textAlign: "center",
                                  position: "absolute",
                                  display: "flex",
                                  width: "100%",
                                  height: "100%",
                                  top: 0,
                                  left: 0,
                                  zIndex: 0, // Pastikan di belakang konten lain
                                  pointerEvents: "none", // Supaya tidak mengganggu interaksi
                                }}
                              >
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    fontSize: "2vw", // Gunakan unit vw agar responsif
                                    fontWeight: "bold",
                                    color: "red",
                                    opacity: 0.35, // Lebih transparan agar tidak terlalu mengganggu
                                    whiteSpace: "nowrap", // Mencegah teks pecah ke bawah
                                    maxWidth: "90%", // Mencegah teks terlalu lebar
                                    overflow: "hidden",
                                  }}
                                >
                                  {t("OrderStatus7Days.Cancelled")}
                                </div>
                              </div>
                            )}
                            {order?.status === "IN_CANCEL" && (
                              <div
                                style={{
                                  textAlign: "center",
                                  position: "absolute",
                                  display: "flex",
                                  width: "100%",
                                  height: "100%",
                                  top: 0,
                                  left: 0,
                                  zIndex: 0, // Pastikan di belakang konten lain
                                  pointerEvents: "none", // Supaya tidak mengganggu interaksi
                                }}
                              >
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    fontSize: "2vw", // Gunakan unit vw agar responsif
                                    fontWeight: "bold",
                                    color: "orange",
                                    opacity: 0.75, // Lebih transparan agar tidak terlalu mengganggu
                                    whiteSpace: "nowrap", // Mencegah teks pecah ke bawah
                                    maxWidth: "90%", // Mencegah teks terlalu lebar
                                    overflow: "hidden",
                                  }}
                                >
                                  {t("OrderStatus7Days.InCancel")}
                                </div>
                              </div>
                            )}
                            {order?.status === "SHIPPING" && (
                              <div
                                style={{
                                  textAlign: "center",
                                  position: "absolute",
                                  display: "flex",
                                  width: "100%",
                                  height: "100%",
                                  top: 0,
                                  left: 0,
                                  zIndex: 0, // Pastikan di belakang konten lain
                                  pointerEvents: "none", // Supaya tidak mengganggu interaksi
                                }}
                              >
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    fontSize: "2vw", // Gunakan unit vw agar responsif
                                    fontWeight: "bold",
                                    color: "green",
                                    opacity: 0.75, // Lebih transparan agar tidak terlalu mengganggu
                                    whiteSpace: "nowrap", // Mencegah teks pecah ke bawah
                                    maxWidth: "90%", // Mencegah teks terlalu lebar
                                    overflow: "hidden",
                                  }}
                                >
                                  {t("OrderStatus7Days.Shipping")}
                                </div>
                              </div>
                            )}
                            {(order?.status === "COMPLETED" ||
                              order?.status === "DELIVERED") && (
                              <div
                                style={{
                                  textAlign: "center",
                                  position: "absolute",
                                  display: "flex",
                                  width: "100%",
                                  height: "100%",
                                  top: 0,
                                  left: 0,
                                  zIndex: 0, // Pastikan di belakang konten lain
                                  pointerEvents: "none", // Supaya tidak mengganggu interaksi
                                }}
                              >
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    fontSize: "2vw", // Gunakan unit vw agar responsif
                                    fontWeight: "bold",
                                    color: "green",
                                    opacity: 0.75, // Lebih transparan agar tidak terlalu mengganggu
                                    whiteSpace: "nowrap", // Mencegah teks pecah ke bawah
                                    maxWidth: "90%", // Mencegah teks terlalu lebar
                                    overflow: "hidden",
                                  }}
                                >
                                  {t("OrderStatus7Days.Completed")}
                                </div>
                              </div>
                            )} */}
                          </td>
                          <td className="w-auto text-wrap">
                            {order.shippingTrackingNumber}
                          </td>
                          <td className="w-auto text-wrap">
                            {order.productName}
                          </td>
                          <td className="w-auto text-wrap">
                            {order.productSKU}
                          </td>
                          <td className="w-auto">
                            <p className="m-0 p-0 d-flex justify-content-center w-fit">
                              {moment(order.printedLabelAt.toDate()).format(
                                "DD/MM/YYYY"
                              )}
                            </p>
                            <p className="m-0 p-0">
                              {moment(order.printedLabelAt.toDate()).format(
                                "HH:mm:ss"
                              )}
                            </p>
                          </td>
                          <td className="w-auto">
                            <p className="m-0 p-0 d-flex justify-content-center w-fit">
                              {order.printedLabelLocatorCode ?? "-"}
                            </p>
                          </td>
                          <td className="w-auto">
                            <p className="m-0 p-0 d-flex justify-content-center w-full">
                              {order.status === "CANCELLED"
                                ? t("OrderStatus7Days.Cancelled")
                                : order.status === "IN_CANCEL"
                                ? t("OrderStatus7Days.InCancel")
                                : order.status === "SHIPPING"
                                ? t("OrderStatus7Days.Shipping")
                                : order.status === "COMPLETED" ||
                                  order.status === "DELIVERED"
                                ? t("OrderStatus7Days.Completed")
                                : order.status === "READY_TO_SHIP"
                                ? t("Value.Pack.Printed")
                                : "-"}
                            </p>
                          </td>
                          {/* <td className="w-auto text-wrap">
                            <div className="d-flex justify-content-center align-items-center">
                              <ForwardInfoComponent data={order} />
                            </div>
                            {order.forwardPushStatuses?.READY_TO_SHIP
                              ?.forwardedAt && (
                              <>
                                <p className="m-0 p-0 d-flex justify-content-center w-fit">
                                  {moment(
                                    order.forwardPushStatuses?.READY_TO_SHIP?.forwardedAt?.toDate()
                                  )?.format("DD/MM/YYYY")}
                                </p>
                                <p className="m-0 p-0">
                                  {moment(
                                    order.forwardPushStatuses?.READY_TO_SHIP?.forwardedAt?.toDate()
                                  )?.format("HH:mm:ss")}
                                </p>
                              </>
                            )}
                          </td> */}
                          <td className="w-auto text-wrap">
                            <button
                              className="w-100 btn btn-primary btn-sm py-2 my-1"
                              onClick={() => {
                                if (order.shippingLabelFile?.url) {
                                  const link = document.createElement("a");
                                  link.href = order.shippingLabelFile.url;
                                  link.download = `shipping-label-${order.ordersn}.pdf`; // Nama file saat didownload
                                  link.target = "_blank"; // Buka di tab baru jika perlu
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                } else {
                                  alert("File tidak tersedia.");
                                }
                              }}
                            >
                              {t("Value.Pack.Print")}
                            </button>
                            {/* {(order?.forwardError ||
                              order?.isForwardSuccess == null) && (
                              <button
                                className="w-100 btn btn-warning btn-sm py-2 my-1"
                                onClick={async () => {
                                  const requestReforward =
                                    order.status === "IN_CANCEL"
                                      ? await reForwardOrderInCancel(
                                          order?.ordersn
                                        )
                                      : await reForwardOrder(order?.ordersn);
                                  if (requestReforward?.statusCode === 200) {
                                    toast(
                                      `${t("OrderList.ReForward.Success")}${
                                        order?.id
                                      }`,
                                      {
                                        type: "success",
                                        position: "top-center",
                                        autoClose: 4000,
                                        hideProgressBar: false,
                                        closeOnClick: true,
                                        pauseOnHover: true,
                                        draggable: true,
                                      }
                                    );
                                  } else {
                                    toast(
                                      `${t("OrderList.ReForward.Failed")}${
                                        order?.id
                                      } Error: ${
                                        requestReforward?.error?.message
                                      }`,
                                      {
                                        type: "error",
                                        position: "top-center",
                                        autoClose: 4000,
                                        hideProgressBar: false,
                                        closeOnClick: true,
                                        pauseOnHover: true,
                                        draggable: true,
                                      }
                                    );
                                  }
                                }}
                              >
                                {t("OrderList.ReForward")}
                              </button>
                            )} */}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="text-center">
                          {t("Order.List.Empty")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <p className="m-0 mt-1 p-0 text-warning text-sm">
                <i className="bi bi-exclamation-triangle text-warning me-2"></i>
                {t("Value.Pack.Scan.Reprint.Info")}
              </p>
            </div>

            {/* Modal Footer */}
            <div className="modal-footer py-3 my-0">
              <button onClick={exportToCSV} className="btn btn-primary my-0">
                {t("ExportOrder.Title")}
              </button>
              <button onClick={onClose} className="btn btn-secondary my-0">
                {t("Common.Button.Cancel")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReprintModal;
